import {
  AbstractBaseUser,
  AbstractCategory,
  AbstractLiveSession,
  AbstractProgram,
  AlgoliaAnytimeArticle,
  ArticleType,
  HttpMethods,
  mapToCard,
  SessionState,
} from "@kvix/shared";
import {
  Button,
  CardList,
  Placeholder,
  SessionSwiper,
  SessionSwiperItem,
} from "@kvix/ui";
import { Container, makeStyles } from "@material-ui/core";
import { addDays, parseJSON } from "date-fns";
import { subDays } from "date-fns/esm";
import queryString from "query-string";
import React, { memo, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { SortOption } from "../../../contexts/algolia";
import { LocationContext } from "../../../contexts/location";
import { isBasicInstructor, KvixUserContext } from "../../../contexts/user";
import { useIsMobile } from "../../../hooks/isMobile";
import { useShowPromotions } from "../../../hooks/promotions";
import { Footer } from "../../Footer";
import { BlogContainer } from "../../partials/BlogContainer";
import { CategoryContainer } from "../../partials/CategoryContainer";
import { Card } from "../../partials/Content/Card";
import { Page } from "../../partials/Page";
import { FeedItem } from "./components/FeedItem";
import { HeroCarousel } from "./components/HeroCarousel";
import { MobileStreamingNow } from "./components/MobileStreamingNow";
import { Promote } from "./components/Promote";
import { WelcomeTitle } from "./components/WelcomeTitle";

const useStyles = makeStyles((theme) => ({
  ContainerPadding: {
    [theme.breakpoints.down("xl")]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
    },
    [theme.breakpoints.down("lg")]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
    },
    [theme.breakpoints.down("md")]: {
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(0),
      paddingLeft: theme.spacing(0),
    },
    [theme.breakpoints.down("xs")]: {
      paddingRight: theme.spacing(0),
      paddingLeft: theme.spacing(0),
    },
  },

  checkIn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  checkInButton: {
    "& span": {
      textTransform: "none",
    },
    height: "44px",
    paddingLeft: 20,
    paddingRight: 20,
    borderRadius: 22,

    "&:active": {
      animation: "$scaleButton linear 200ms ",
    },
  },
  "@keyframes scaleButton": {
    "0%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.2)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
}));

const ExploreButton: React.FC<{ url: string }> = memo((props) => {
  const classes = useStyles();
  const { push } = useHistory();
  const onClick = () => {
    push(props.url);
  };
  return (
    <div className={classes.checkIn}>
      {/* <Button className={classes.checkInButton} onClick={onClick}> */}
      <Button variant="contained" onClick={onClick}>
        {props.children}
      </Button>
    </div>
  );
});

const StartFeed: React.FC = (props) => {
  const classes = useStyles();
  const showPromotions = useShowPromotions();
  const { t, ready } = useTranslation("startPage");
  const NO_OF_CARDS = 10;
  const {
    favouritedProgramIds,
    user,
    actions,
    favouritedSessionIds,
    followedInstructors,
    sessions,
  } = useContext(KvixUserContext);
  const now = new Date();
  const isMobile = useIsMobile();
  const location = useContext(LocationContext);
  const todaysSessionQuery = useMemo(() => {
    return queryString.stringify({ selectedDate: now.toISOString() });
  }, [now]);
  const tomorrowsSessionQuery = useMemo(() => {
    return queryString.stringify({
      selectedDate: addDays(now, 1).toISOString(),
    });
  }, [now]);

  if (!ready) {
    return null;
  }

  return (
    <Container
      maxWidth="xl"
      className={classes.ContainerPadding}
      // style={{ padding: isMobile ? "0px 15px" : "0px 32px" }}
    >
      {/* MOBILE STREAMING NOW */}
      {isMobile && (
        <FeedItem
          srcConfig={{
            fetch: {
              url: `/api/session/current`,
              method: HttpMethods.GET,
            },
          }}
          // mt={40}
          renderPlaceholder={() => <Placeholder.CardFeed cards={5} rows={1} />}
          renderElement={(sessions: AbstractLiveSession[]) => {
            return <MobileStreamingNow sessions={sessions} />;
          }}
        />
      )}

      {/* TODAY'S SESSIONS */}
      <FeedItem
        srcConfig={{
          fetch: {
            url: `/api/session/day?${todaysSessionQuery}`,
            method: HttpMethods.GET,
          },
        }}
        renderPlaceholder={() => <Placeholder.CardFeed cards={5} rows={1} />}
        renderElement={(sessions: AbstractLiveSession[]) => {
          const startIndex = sessions?.findIndex(
            (session) =>
              session.state === SessionState.Running ||
              parseJSON(session.start) > now
          );

          const initialIndex = sessions[startIndex - 1]
            ? startIndex - 1
            : startIndex;

          const isAnytime = (session: AbstractLiveSession) => {
            return (
              session.state === SessionState.Finished &&
              (session.mp4Url || session.hlsUrl)
            );
          };

          return (
            <>
              {sessions?.length > 0 && (
                <CardList
                  title={t("feeds.sessions.todaysClasses")}
                  titleUrl="/sessions"
                  isStartpage
                  initialCard={initialIndex}
                >
                  {sessions?.map((session) => (
                    <Card
                      type={
                        isAnytime(session)
                          ? ArticleType.ANYTIME
                          : ArticleType.SESSION
                      }
                      options={mapToCard({ session })}
                      key={session.id}
                      displayDateLabel
                      wide={!isMobile}
                      toggleFavourite={
                        isAnytime(session) && user
                          ? () => actions.toggleFavourite({ session })
                          : null
                      }
                    />
                  ))}
                </CardList>
              )}
            </>
          );
        }}
      />

      {/* TOMORROW'S SESSIONS */}
      <FeedItem
        srcConfig={{
          fetch: {
            url: `/api/session/day?${tomorrowsSessionQuery}`,
            method: HttpMethods.GET,
          },
        }}
        // mt={40}
        renderPlaceholder={() => <Placeholder.CardFeed cards={5} rows={1} />}
        renderElement={(sessions: AbstractLiveSession[]) => (
          <>
            {sessions?.length > 0 && (
              <CardList
                title={t("feeds.sessions.tomorrowsClasses")}
                isStartpage
                titleUrl="/sessions"
              >
                {sessions?.map((session) => (
                  <Card
                    type={ArticleType.SESSION}
                    options={mapToCard({ session })}
                    key={session.id}
                    displayDateLabel
                  />
                ))}
              </CardList>
            )}
          </>
        )}
      />

      {/* EXPLORE LIVESESSIONS BUTTON */}
      {/* <FeedItem
        // mt={40}
        renderElement={() => (
          <ExploreButton url="/sessions">
            {t("feeds.sessions.upcomingDaysButton")}
          </ExploreButton>
        )}
      />
      {!user && <>{showPromotions && <Promote />}</>} */}
      {/* FAVOURITE PROGRAMS */}
      {!!favouritedProgramIds?.length && (
        <FeedItem
          srcConfig={{
            fetch: {
              url: `/api/program/favourited`,
              method: HttpMethods.POST,
              body: JSON.stringify({ ids: favouritedProgramIds }),
              headers: {
                "Content-Type": "application/json",
              },
            },
          }}
          // mt={60}
          renderPlaceholder={() => <Placeholder.CardFeed cards={5} rows={1} />}
          renderElement={(programs: AbstractProgram[]) => (
            <>
              {!!programs?.length && (
                <CardList
                  title={t("feeds.programs.favouritePrograms")}
                  isStartpage
                  titleUrl="/me/favourites"
                >
                  {programs.map((program) => (
                    <Card
                      type={ArticleType.PROGRAM}
                      options={mapToCard({ program })}
                      key={program.id}
                      toggleFavourite={() =>
                        actions.toggleFavourite({ programId: program.id })
                      }
                    />
                  ))}
                </CardList>
              )}
            </>
          )}
        />
      )}

      {/* FAVOURITE RECORDINGS (ANYTIME & KVIXTV) */}
      {!!favouritedSessionIds?.length && (
        <FeedItem
          srcConfig={{
            fetch: {
              url: "/api/session/favourited",
              method: HttpMethods.POST,
              body: JSON.stringify({ ids: favouritedSessionIds }),
              headers: {
                "Content-Type": "application/json",
              },
            },
          }}
          // mt={40}
          renderPlaceholder={() => (
            <Placeholder.WideCardFeed cards={5} rows={1} />
          )}
          renderElement={(sessions: AbstractLiveSession[]) => (
            <SessionSwiper
              title={t("feeds.sessions.favouriteStreams", {
                name: user.name.first,
              })}
              titleUrl={"/me/favourites"}
              sessions={sessions}
              user={user}
              favouritedSessionIds={favouritedSessionIds}
              toggleFavourite={actions.toggleFavourite}
            >
              {(itemProps) => <SessionSwiperItem {...itemProps} />}
            </SessionSwiper>
          )}
        />
      )}

      {/* FAVOURITE INSTRUCTORS */}
      {followedInstructors && (
        <FeedItem
          srcConfig={{
            fetch: {
              url: `/api/instructor/favourited`,
              method: HttpMethods.POST,
              body: JSON.stringify({ ids: Object.keys(followedInstructors) }),
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
            },
          }}
          // mt={40}
          renderPlaceholder={() => (
            <Placeholder.InstructorFeed cards={5} rows={1} />
          )}
          renderElement={(instructors: AbstractBaseUser[]) => {
            const isFollowing = (instructor: AbstractBaseUser) => {
              if (instructor?.instructorProfile && followedInstructors) {
                return followedInstructors.hasOwnProperty(
                  instructor.instructorProfile.id
                );
              }
              return null;
            };

            return (
              <>
                {!!instructors?.length && (
                  <CardList
                    title={t("feeds.instructors.favouriteInstructors")}
                    titleUrl={"/me/favourites"}
                    isStartpage
                  >
                    {instructors?.map((instructor) => {
                      return (
                        <Card
                          type={
                            isBasicInstructor(instructor)
                              ? ArticleType.BASIC_INSTRUCTOR
                              : ArticleType.INSTRUCTOR
                          }
                          options={mapToCard({
                            instructor,
                          })}
                          key={instructor.id}
                        />
                      );
                    })}
                  </CardList>
                )}
              </>
            );
          }}
        />
      )}

      {/* RECENTLY WATCHED RECORDINGS (ANYTIME & KVIXTV) */}
      {!!Object.keys(sessions.watched).length && (
        <FeedItem
          srcConfig={{
            fetch: {
              url: `/api/session/watched?max=${NO_OF_CARDS}`,
              method: HttpMethods.GET,
            },
          }}
          // mt={40}
          renderPlaceholder={() => (
            <Placeholder.WideCardFeed cards={isMobile ? 2 : 5} rows={1} />
          )}
          renderElement={(sessions: AbstractLiveSession[]) => (
            <SessionSwiper
              title={t("feeds.sessions.streamItAgain", {
                name: user.name.first,
              })}
              titleUrl={"/sessions"}
              sessions={sessions}
              user={user}
              favouritedSessionIds={favouritedSessionIds}
              toggleFavourite={actions.toggleFavourite}
            >
              {(itemProps) => <SessionSwiperItem {...itemProps} />}
            </SessionSwiper>
          )}
        />
      )}

      {/* TRENDING ANYTIME */}
      <FeedItem
        srcConfig={{
          algolia: {
            query: "",
            filters: `type:"anytime" AND startTimestamp > ${subDays(
              now,
              10
            ).getTime()}`,
            hitsPerPage: NO_OF_CARDS,
            sortBy: SortOption.POPULARITY,
          },
        }}
        // mt={40}
        renderPlaceholder={() => <Placeholder.CardFeed cards={5} rows={1} />}
        renderElement={(anytimes: AlgoliaAnytimeArticle[]) => (
          <>
            {!!anytimes?.length && (
              <CardList
                title={t("feeds.anytime.trendingAnytime")}
                isStartpage
                titleUrl="/anytime"
              >
                {anytimes?.map((anytime) => (
                  <Card
                    type={ArticleType.ANYTIME}
                    options={anytime}
                    key={anytime.id}
                    toggleFavourite={
                      user
                        ? () => actions.toggleFavourite({ session: anytime })
                        : null
                    }
                  />
                ))}
              </CardList>
            )}
          </>
        )}
      />

      {/* PROGRAM CATEGORIES */}
      <FeedItem
        srcConfig={{
          fetch: {
            url: `/api/category/all`,
            method: HttpMethods.GET,
          },
        }}
        // mt={40}
        renderPlaceholder={() => <Placeholder.CardFeed cards={5} rows={1} />}
        renderElement={(categories: AbstractCategory[]) => (
          <CategoryContainer
            title={t("feeds.programs.title")}
            description={t("feeds.programs.description")}
            explore={{ text: t("feeds.programs.explore"), url: "/programs" }}
            categories={categories}
            noMargin
          />
        )}
      />

      {/* BLOGPOSTS */}
      <FeedItem
        renderElement={() => (
          <BlogContainer
            title={t("feeds.blog.title")}
            description={t("feeds.blog.description")}
            explore={{ text: t("feeds.blog.explore"), url: "/blog" }}
            noMargin
          />
        )}
        // mt={40}
      />
    </Container>
  );
};

export const Start: React.FC = (props) => {
  const { user } = useContext(KvixUserContext);

  return (
    <Page>
      {!user && <WelcomeTitle />}
      <HeroCarousel user={user} />
      <StartFeed />
      <Footer />
    </Page>
  );
};
